/** Bandeja de tareas con segmentos y búsqueda (shape real: sin título ni entidad). */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileCard } from '@/components/MobileCard';
import { StatusBadge } from '@/components/StatusBadge';
import { SearchInput } from '@/components/SearchInput';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import { PullIndicator } from '@/components/PullIndicator';
import { useMobileTasksQuery } from '@/hooks/useMobileTasks';
import { usePullToRefresh } from '@/hooks/usePullToRefresh';
import { useMobileAuth } from '@/state/MobileAuthContext';
import { formatRelativeDate, isOverdue } from '@/utils/format';

function shortId(id: string): string {
  return id.slice(0, 8);
}

function responsibleName(task: { assignee_snapshot?: { display_name?: string } | null }): string {
  const name = task.assignee_snapshot?.display_name?.trim();
  return name ? name : 'Sin responsable';
}

function assignedLabel(assignedAt: string | null | undefined): string {
  if (!assignedAt) return 'Sin fecha de asignación';
  return `Asignada ${formatRelativeDate(assignedAt)}`;
}

export function TasksPage() {
  const { user, hasPermission } = useMobileAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'open' | 'done'>('open');
  const [search, setSearch] = useState('');
  const tasksQuery = useMobileTasksQuery(tab);
  const canView = hasPermission('workflow_tasks.view_own');
  const { bindRef, pulling, refreshing } = usePullToRefresh({
    onRefresh: () => tasksQuery.refetch(),
    disabled: user?.active_tenant_id == null || !canView,
  });

  const filteredTasks = useMemo(() => {
    const items = tasksQuery.data ?? [];
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      const short = shortId(t.id).toLowerCase();
      const responsible = (t.assignee_snapshot?.display_name ?? '').toLowerCase();
      const documentCode = (t.document_code ?? '').toLowerCase();
      const stepName = (t.step_name ?? '').toLowerCase();
      const status = t.status.toLowerCase();
      return (
        short.includes(q) ||
        t.id.toLowerCase().includes(q) ||
        documentCode.includes(q) ||
        stepName.includes(q) ||
        responsible.includes(q) ||
        status.includes(q)
      );
    });
  }, [tasksQuery.data, search]);

  return (
    <MobileLayout title="Mis tareas" subtitle={tab === 'open' ? 'Pendientes' : 'Resueltas'}>
      <div ref={bindRef} className="space-y-4">
      <PullIndicator pulling={pulling} refreshing={refreshing} />
      <div
        role="radiogroup"
        aria-label="Filtrar tareas por estado"
        className="mb-4 flex items-center gap-2 rounded-full bg-bg-tertiary p-1"
      >
        {(['open', 'done'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={tab === value}
            onClick={() => setTab(value)}
            className={`flex h-11 min-h-[44px] flex-1 items-center justify-center gap-1 rounded-full text-sm font-semibold transition-all active:scale-[0.97] ${
              tab === value
                ? 'bg-white text-text-primary shadow-card'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            <MobileIcon name={value === 'open' ? 'timer' : 'check_circle'} className="h-5 w-5 shrink-0" />
            <span className="truncate">{value === 'open' ? 'Pendientes' : 'Resueltas'}</span>
          </button>
        ))}
      </div>

      {canView && (
        <div className="mb-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por documento, paso o responsable..."
          />
        </div>
      )}

      {!canView && (
        <EmptyState
          icon="lock_person"
          title="Sin permisos"
          description="No tienes acceso a la bandeja de tareas en esta empresa."
        />
      )}

      {user?.active_tenant_id === null && canView && (
        <EmptyState
          icon="business"
          title="Elige una empresa"
          description="Selecciona la empresa para ver tus tareas."
          actionLabel="Elegir empresa"
          onAction={() => navigate('/m/company')}
        />
      )}

      {canView && user?.active_tenant_id !== null && (
        <>
          {tasksQuery.isPending && <CardListSkeleton count={4} />}

          {tasksQuery.isError && (
            <EmptyState
              icon="error"
              title="No se pudo cargar"
              description="Verifica tu conexión e intenta de nuevo."
              actionLabel="Reintentar"
              onAction={() => tasksQuery.refetch()}
            />
          )}

          {!tasksQuery.isPending && !tasksQuery.isError && filteredTasks.length === 0 && (
            <EmptyState
              icon={search ? 'search_off' : tab === 'open' ? 'celebration' : 'inbox'}
              title={search ? 'Sin resultados' : tab === 'open' ? '¡Todo al día!' : 'Sin tareas resueltas'}
              description={
                search
                  ? 'No hay tareas que coincidan con tu búsqueda.'
                  : tab === 'open'
                    ? 'No tienes tareas pendientes por aprobar.'
                    : 'Aún no has resuelto ninguna tarea.'
              }
            />
          )}

          <div className="space-y-3">
            {filteredTasks.map((task) => {
              const overdue = tab === 'open' && isOverdue(task.due_at);
              const responsible = responsibleName(task);
              return (
                <Link key={task.id} to={`/m/tasks/${task.id}`} className="block">
                  <MobileCard
                    title={task.document_code || `Tarea ${shortId(task.id)}`}
                    subtitle={`${task.step_name || 'Tarea'} · ${responsible}`}
                    icon="task"
                    iconColor={overdue ? 'error' : 'primary'}
                    rightContent={<StatusBadge status={task.status} size="sm" />}
                    metadata={[
                      { label: 'Asignada', value: assignedLabel(task.assigned_at).replace('Asignada ', '') },
                      ...(task.due_at
                        ? [
                            {
                              label: overdue ? 'Vencido' : 'Vence',
                              value: formatRelativeDate(task.due_at),
                            },
                          ]
                        : [{ label: 'Plazo', value: 'Sin plazo' }]),
                    ]}
                    className={overdue ? 'border-l-4 border-l-error' : undefined}
                  />
                </Link>
              );
            })}
          </div>
        </>
      )}
      </div>
    </MobileLayout>
  );
}
